import { type ComponentPropsWithoutRef, useState, useEffect, useCallback } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/DhanurvedaProgram.css';
import { headingId } from '../lib/headingId';
import type { PageContent } from '../types/content';
import ShareableHeading from './ShareableHeading';
import ContentRenderer from './ContentRenderer';
import programContent from '../content/dhanurveda.yaml';

interface Props {
  onNavigate: (path: string) => void;
}

const PHASES = [
  {
    numKey: 'dhanurveda.program.phase1.num',
    titleKey: 'dhanurveda.program.phase1.title',
    subtitleKey: 'dhanurveda.program.phase1.subtitle',
    topicKeys: [
      'dhanurveda.program.phase1.topic1',
      'dhanurveda.program.phase1.topic2',
      'dhanurveda.program.phase1.topic3',
      'dhanurveda.program.phase1.topic4',
      // 'dhanurveda.program.phase1.topic5',
    ],
    markdownPath: '/textbooks/dh-ved-01.md',
  },
  {
    numKey: 'dhanurveda.program.phase2.num',
    titleKey: 'dhanurveda.program.phase2.title',
    subtitleKey: 'dhanurveda.program.phase2.subtitle',
    topicKeys: [
      'dhanurveda.program.phase2.topic1',
      'dhanurveda.program.phase2.topic2',
      'dhanurveda.program.phase2.topic3',
      'dhanurveda.program.phase2.topic4',
      'dhanurveda.program.phase2.topic5',
    ],
    markdownPath: '/textbooks/dh-ved-02.md',
  },
  {
    numKey: 'dhanurveda.program.phase3.num',
    titleKey: 'dhanurveda.program.phase3.title',
    subtitleKey: 'dhanurveda.program.phase3.subtitle',
    topicKeys: [
      'dhanurveda.program.phase3.topic1',
      'dhanurveda.program.phase3.topic2',
      'dhanurveda.program.phase3.topic3',
      'dhanurveda.program.phase3.topic4',
      'dhanurveda.program.phase3.topic5',
    ],
    markdownPath: '/textbooks/dh-ved-03.md',
  },
  {
    numKey: 'dhanurveda.program.phase4.num',
    titleKey: 'dhanurveda.program.phase4.title',
    subtitleKey: 'dhanurveda.program.phase4.subtitle',
    topicKeys: [
      'dhanurveda.program.phase4.topic1',
      'dhanurveda.program.phase4.topic2',
      'dhanurveda.program.phase4.topic3',
      'dhanurveda.program.phase4.topic4',
      'dhanurveda.program.phase4.topic5',
    ],
    markdownPath: '/textbooks/dh-ved-04.md',
  },
];

const BATCHES = [
  { id: 'spring2025', season: '🌸', featured: true, popular: true },
  { id: 'autumn2025', season: '🍂', featured: false, popular: false },
  { id: 'winter2025', season: '❄️', featured: false, popular: false },
] as const;



const INTRO_COMPONENTS: Components = {
  h2: ({ children, ...props }: ComponentPropsWithoutRef<'h2'>) => (
    <ShareableHeading id={headingId(children)} as="h2" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
  h3: ({ children, ...props }: ComponentPropsWithoutRef<'h3'>) => (
    <ShareableHeading id={headingId(children)} as="h3" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
  h4: ({ children, ...props }: ComponentPropsWithoutRef<'h4'>) => (
    <ShareableHeading id={headingId(children)} as="h4" {...(props as object)}>
      {children}
    </ShareableHeading>
  ),
};

export default function DhanurvedaProgram({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const [mdModal, setMdModal] = useState<{ title: string; content: string } | null>(null);

  const openMarkdown = useCallback(async (path: string, title: string) => {
    try {
      const res = await fetch(path);
      const text = await res.text();
      setMdModal({ title, content: text });
    } catch {
      setMdModal({ title, content: '_Could not load content._' });
    }
  }, []);

  useEffect(() => {
    if (!mdModal) return;
    const close = (e: KeyboardEvent) => { if (e.key === 'Escape') setMdModal(null); };
    document.addEventListener('keydown', close);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', close);
      document.body.style.overflow = '';
    };
  }, [mdModal]);
  const phaseLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const introDescription = t('dhanurveda.program.description');
  const overviewContent: PageContent = locale === 'en'
    ? {
        sections: [
          {
            id: 'darshan', heading: t('dhanurveda.program.darshan.heading'), shareable: true,
            statements: [
              { id: 'dhanurveda-not-weapon', text: t('dhanurveda.program.darshan.stmt1'), shareable: true },
              { id: 'tradition-age', text: t('dhanurveda.program.darshan.stmt2'), shareable: true },
              { text: t('dhanurveda.program.darshan.note') },
            ],
          },
          {
            id: 'lakshy', heading: t('dhanurveda.program.lakshy.heading'), shareable: true,
            statements: [
              { id: 'goal-focus', text: t('dhanurveda.program.lakshy.stmt1'), shareable: true },
              { id: 'goal-culture', text: t('dhanurveda.program.lakshy.stmt2'), shareable: true },
              { text: t('dhanurveda.program.lakshy.note'), type: 'note' as const },
            ],
          },
          {
            id: 'safety', heading: t('dhanurveda.program.safety_section.heading'), shareable: true,
            statements: [
              { id: 'arms-act-note', text: t('dhanurveda.program.safety_section.stmt1'), shareable: true, type: 'note' as const },
              { id: 'supervised-only', text: t('dhanurveda.program.safety_section.stmt2'), shareable: true },
            ],
          },
        ],
      }
    : { sections: programContent.sections.filter((s: { id: string }) => ['darshan', 'lakshy', 'safety'].includes(s.id)) };
  return (
    <>
    <div className="dhanurveda-page">
      <button className="dv-back-btn" onClick={() => onNavigate('/products')}>
        {t('dhanurveda.program.back')}
      </button>

      {/* ── Hero ── */}
      <div className="dv-hero">
        <span className="dv-hero-badge">{t('dhanurveda.program.badge')}</span>
        <h1>{t('dhanurveda.program.title')}</h1>
        <p className="dv-hero-tagline">
          {t('dhanurveda.program.tagline')}
        </p>
        <div className="dv-hero-meta">
          <span className="dv-meta-pill">{t('dhanurveda.program.meta.location')}</span>
          <span className="dv-meta-pill">{t('dhanurveda.program.meta.duration')}</span>
          <span className="dv-meta-pill">{t('dhanurveda.program.meta.capacity')}</span>
          <span className="dv-meta-pill">{t('dhanurveda.program.meta.age')}</span>
          <span className="dv-meta-pill">{t('dhanurveda.program.meta.equipment')}</span>
        </div>
        <div className="dv-bow-icon" aria-hidden>🏹</div>
      </div>

      <div className="dv-intro-panel">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={INTRO_COMPONENTS}>
          {introDescription}
        </ReactMarkdown>
      </div>

      {/* ── Philosophy (YAML-driven: sections darshan + lakshy) ── */}
      <div className="dv-section">
        {/* Shloka kept as JSX — formatting not representable in plain YAML text */}
        <p className="dv-shloka">
          {t('dhanurveda.program.shloka.line1')}<br />
          {t('dhanurveda.program.shloka.line2')}
        </p>
        <p className="dv-shloka-source">{t('dhanurveda.program.shlokaSource')}</p>
        {/* ContentRenderer renders YAML sections with hover share icons automatically */}
        <ContentRenderer
          content={overviewContent}
          className="dv-yaml-content"
        />
      </div>

      {/* ── Curriculum ── */}
      <div className="dv-section">
        <ShareableHeading id="pathyakram" as="h2" className="dv-section-title">{t('dhanurveda.program.curriculum.title')}</ShareableHeading>
        <p className="dv-section-subtitle">{t('dhanurveda.program.curriculum.subtitle')}</p>
        <div className="dv-phases">
          {PHASES.map((phase) => (
            <div
              className={`dv-phase-card${phase.markdownPath ? ' dv-phase-card--readable' : ''}`}
              key={phase.numKey}
            >
              <span className="dv-phase-num">{phaseLabel(phase.numKey, '')}</span>
              <h3 className="dv-phase-title">{phaseLabel(phase.titleKey, '')}</h3>
              <p className="dv-phase-subtitle">{phaseLabel(phase.subtitleKey, '')}</p>
              <ul className="dv-phase-topics">
                {phase.topicKeys.map((key) => (
                  <li key={key}>{phaseLabel(key, key)}</li>
                ))}
              </ul>
              {phase.markdownPath && (
                <button
                  className="dv-phase-read-btn"
                  onClick={() => openMarkdown(phase.markdownPath!, phaseLabel(phase.titleKey, 'मूल पाठ'))}
                >
                  📖 {t('dhanurveda.program.read_btn')}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Batches ── */}
      <div className="dv-section">
        <ShareableHeading id="batches" as="h2" className="dv-section-title">{t('dhanurveda.program.batches.title')}</ShareableHeading>
        <div className="dv-batches">
          {BATCHES.map((batch) => (
            <div
              className={`dv-batch-card${batch.featured ? ' featured' : ''}`}
              key={batch.id}
            >
              {batch.popular && <span className="dv-batch-popular">{t('dhanurveda.program.batches.popular')}</span>}
              <div className="dv-batch-season">{batch.season}</div>
              <div className="dv-batch-name">{t(`dhanurveda.program.batch.${batch.id}.name`)}</div>
              <div className="dv-batch-dates">{t(`dhanurveda.program.batch.${batch.id}.dates`)}</div>
              <span className="dv-batch-seats">{t(`dhanurveda.program.batch.${batch.id}.seats`)}</span>
              <div className="dv-batch-price">{t(`dhanurveda.program.batch.${batch.id}.price`)}</div>
              <div className="dv-batch-price-note">{t(`dhanurveda.program.batch.${batch.id}.note`)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Location ── */}
      <div className="dv-section">
        <ShareableHeading id="location" as="h2" className="dv-section-title">{t('dhanurveda.program.location.title')}</ShareableHeading>
        <div className="dv-location">
          <div className="dv-location-icon">🏔️</div>
          <div className="dv-location-info">
            <h3>{t('dhanurveda.program.location.center')}</h3>
            <p>
              {t('dhanurveda.program.location.body')}
            </p>
            <p>
              <strong>{t('dhanurveda.program.location.rail')}</strong> {t('dhanurveda.program.location.railValue')} |{' '}
              <strong>{t('dhanurveda.program.location.air')}</strong> {t('dhanurveda.program.location.airValue')}
            </p>
            <div className="dv-location-tags">
              <span className="dv-location-tag">{t('dhanurveda.program.location.tagRange')}</span>
              <span className="dv-location-tag">{t('dhanurveda.program.location.tagSafe')}</span>
              <span className="dv-location-tag">{t('dhanurveda.program.location.tagLibrary')}</span>
              <span className="dv-location-tag">{t('dhanurveda.program.location.tagFood')}</span>
              <span className="dv-location-tag">{t('dhanurveda.program.location.tagFirstAid')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── पुरोहित ── */}
      <div className="dv-section dv-purohit-section">
        <ShareableHeading id="purohit" as="h2" className="dv-section-title">
          {t('dhanurveda.program.purohit.title')}
        </ShareableHeading>

        <div className="dv-purohit-hero-wrap">
          <img
            src="/purohita/purohita-hero.webp"
            alt="पण्डित सन्दीप शर्मा — अघंजर महादेव मंदिर, धर्मशाला"
            className="dv-purohit-hero-img"
          />
          <div className="dv-purohit-hero-overlay">
            <span className="dv-purohit-hero-name">{t('dhanurveda.program.purohit.heroName')}</span>
          </div>
        </div>

        <div className="dv-purohit-card">
          <div className="dv-purohit-photo-wrap">
            <img
              src="/purohita/purohita-profile.webp"
              alt="पण्डित सन्दीप शर्मा"
              className="dv-purohit-photo"
            />
          </div>
          <div className="dv-purohit-bio">
            <h3 className="dv-purohit-name">{t('dhanurveda.program.purohit.name')}</h3>
            <p>
              {t('dhanurveda.program.purohit.bio1')}
            </p>
            <p className="dv-purohit-closing">
              {t('dhanurveda.program.purohit.bio2')}
            </p>
          </div>
        </div>
      </div>

      {/* ── Prerequisites ── */}
      <div className="dv-section">
        <ShareableHeading id="prerequisites" as="h2" className="dv-section-title">{t('dhanurveda.program.prerequisites.title')}</ShareableHeading>
        <div className="dv-prereq-list">
          {[
            { icon: '🎂', text: t('dhanurveda.program.prerequisites.age') },
            { icon: '❤️', text: t('dhanurveda.program.prerequisites.health') },
            { icon: '💪', text: t('dhanurveda.program.prerequisites.fitness') },
            { icon: '📖', text: t('dhanurveda.program.prerequisites.language') },
            { icon: '🎯', text: t('dhanurveda.program.prerequisites.experience') },
            { icon: '👔', text: t('dhanurveda.program.prerequisites.clothing') },
          ].map((item) => (
            <div className="dv-prereq-item" key={item.text}>
              <span className="dv-prereq-icon">{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
        <div className="dv-safety-note">
          {t('dhanurveda.program.safety')}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="dv-cta-panel">
        <h2>{t('dhanurveda.program.cta.title')}</h2>
        <p>{t('dhanurveda.program.cta.body')}</p>
        <div className="dv-cta-buttons">
          <button
            className="dv-btn-primary"
            onClick={() => onNavigate('/rajdharma/dhanurveda/booking')}
          >
            {t('dhanurveda.program.cta.book')}
          </button>
          <button
            className="dv-btn-secondary"
            onClick={() => onNavigate('/rajdharma/dhanurveda/terms')}
          >
            {t('dhanurveda.program.cta.terms')}
          </button>
        </div>
        <p className="dv-cta-login-note">
          {t('dhanurveda.program.cta.login')}
        </p>
      </div>
    </div>

      {/* ── Markdown source text modal ── */}
      {mdModal && (
        <div className="dv-md-overlay" role="dialog" aria-modal="true" aria-label={mdModal.title} onClick={() => setMdModal(null)}>
          <div className="dv-md-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="dv-md-sheet-header">
              <span className="dv-md-sheet-title">{mdModal.title}</span>
              <button className="dv-md-close-btn" onClick={() => setMdModal(null)} aria-label="Close">✕</button>
            </div>
            <div className="dv-md-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {mdModal.content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
